import { getApi, putApi } from "../../../../apis/api";
import {
  GET_QUEUE_OPTIONS,
  GET_QUEUE_TICKETS,
  QUEUE_VIEW_GROUPS,
  SEARCH_QUEUE_ORDERS,
  UPDATE_QUEUE_TICKET,
} from "../../../../apis/end-points";
import { HelpdeskPageMeta } from "../../../service-config/HelpdeskShared/HelpdeskPager";
import { HelpdeskServiceResponse } from "../../../service-config/HelpdeskShared/mockRequest";
import { isResolvedTab, STATE_TABS } from "../queueConstants";
import {
  OrderOption,
  QueueFilters,
  QueueOptions,
  QueuePatch,
  QueueTicket,
  QueueView,
  ViewGroupOption,
} from "../queueTypes";

/**
 * Data access for Communications › Unassigned (helpdesk-service, KAN-1330–1333).
 * Filtering and paging happen server-side.
 */

export interface QueuePage {
  tickets: QueueTicket[];
  pagination: HelpdeskPageMeta | null;
}

/**
 * One page of a listing that doesn't list helpdesk-service's tickets (Spot Quotes' mock, Spot Quotes listing
 * Decision D6); `page` / `limit` are the page pagination bar's, `filters` the applied system filter (D27).
 */
export type QueuePageSource = (
  page: number,
  limit: number,
  filters: QueueFilters
) => Promise<HelpdeskServiceResponse<QueuePage>>;

/** The applied filter as the listing APIs take it (KAN-1333): one comma-separated param per field with values. */
export const setFilterParams = (query: URLSearchParams, filters: QueueFilters): URLSearchParams => {
  (Object.keys(filters) as (keyof QueueFilters)[]).forEach((key) => {
    if (filters[key].length) query.set(key, filters[key].join(","));
  });
  return query;
};

/** `page` / `limit` are the page pagination bar's (Redux pagination slice). */
export const fetchQueueTickets = async (
  { tab, groupId }: QueueView,
  filters: QueueFilters,
  page: number,
  limit: number
): Promise<HelpdeskServiceResponse<QueuePage>> => {
  // A Resolved tab is its view's Open tab with state=resolved (KAN-1325); a viewed group is view=group (KAN-1362)
  const view = STATE_TABS[tab]?.[0] ?? tab;
  const query = new URLSearchParams({ view, page: String(page), limit: String(limit) });
  if (groupId) query.set("groupId", groupId);
  if (isResolvedTab(tab)) query.set("state", "resolved");
  setFilterParams(query, filters);
  const response = await getApi<QueueTicket[]>(`${GET_QUEUE_TICKETS}?${query.toString()}`);
  if (!response.success) return { success: false, error: response.error };
  return {
    success: true,
    data: { tickets: response.data ?? [], pagination: (response.pagination as HelpdeskPageMeta) ?? null },
  };
};

export const fetchQueueOptions = (): Promise<HelpdeskServiceResponse<QueueOptions>> =>
  getApi<QueueOptions>(GET_QUEUE_OPTIONS);

/** View Another Group (KAN-1362): every group but Exception, with its open tickets and whether it is viewed. */
export const fetchViewGroups = (): Promise<HelpdeskServiceResponse<ViewGroupOption[]>> =>
  getApi<ViewGroupOption[]>(QUEUE_VIEW_GROUPS);

/** Saves the groups listed under Communications › Groups; resolves to the pop-up's list. */
export const saveViewGroups = (groupIds: string[]): Promise<HelpdeskServiceResponse<ViewGroupOption[]>> =>
  putApi<ViewGroupOption[]>(QUEUE_VIEW_GROUPS, { groupIds });

/** Orders for the Order Number dropdown, newest first, 50 a page; an empty `q` lists all (KAN-1332). */
export const searchOrders = (q: string, page = 1): Promise<HelpdeskServiceResponse<OrderOption[]>> =>
  getApi<OrderOption[]>(`${SEARCH_QUEUE_ORDERS}?q=${encodeURIComponent(q)}&page=${page}`);

/** Saves one change; resolves to the updated row. */
export const updateQueueTicket = (
  ticketId: string,
  patch: QueuePatch
): Promise<HelpdeskServiceResponse<QueueTicket>> =>
  putApi<QueueTicket>(`${UPDATE_QUEUE_TICKET}${encodeURIComponent(ticketId)}`, patch);
